import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import BookkeepingService from "../../../../../services/BookkeepingService";
import CheckAgainButton from "./CheckAgainButton";
import WComponent from "../../../../common/WComponent";
import "./evidence-slot.css";

const LOOK_FOR_EACH_TONE = {
	agreed:    { cls: "evidence-slot__vmeta--ok",      icon: Icon.CheckCircle },
	exception: { cls: "evidence-slot__vmeta--bad",     icon: Icon.AlertCircle },
	caution:   { cls: "evidence-slot__vmeta--warn",    icon: Icon.AlertTriangle },
	unchecked: { cls: "evidence-slot__vmeta--neutral", icon: Icon.HelpCircle },
};

function readVerdictLook(verification) {
	if (!verification || !verification.verdict) return null;
	let look = LOOK_FOR_EACH_TONE[verification.verdict.tone] || LOOK_FOR_EACH_TONE.unchecked;
	return { cls: look.cls, icon: look.icon, label: verification.verdict.text };
}

/**
 * EvidenceSlot — one expected piece of supporting documentation for a lead sheet row.
 *
 * Props:
 *   slot   — { code, label, required, waived, justification, source }
 *   doc    — uploaded Document for this slot, or null
 *   uploading — boolean (during upload)
 *   onUpload(file)
 *   onDelete()
 *   onToggleReviewed()
 *
 * State drives appearance:
 *   waived         → amber, justification visible inline
 *   doc + reviewed → green, file name + reviewed checkmark
 *   doc unreviewed → grey, file name + circle
 *   no doc, required    → upload button + small "Waive" link
 *   no doc, optional    → upload button only
 */
function CheckGlyph({ ok }) {
	let Glyph = ok ? Icon.Check : Icon.X;
	return <Glyph size={11} color={ok ? "#2e7d32" : "#c62828"} />;
}

function fmtAmt(amt) {
	if (amt == null) return "—";
	let n = Number(amt);
	let abs = Math.abs(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	return (n < 0 ? "-$" : "$") + abs;
}

const METHOD_LABELS = {
	manual: "Preparer",
	transfer: "Transfer",
	gl_reference: "General Ledger",
	dbf_gifi: "General Ledger",
	ai: "AI",
	unmatched: "Unmatched",
};

const RECON_FILTERS = [
	{ id: "all", label: "All" },
	{ id: "to_review", label: "To Review" },
	{ id: "approved", label: "Approved" },
];

class ReconClassificationCell extends WComponent {
	state = {
		open: false,
		search: "",
		accounts: null,
	};

	handleOpen = () => {
		let { accounts, open } = this.state;
		if (!accounts) {
			// Lazy-load the canonical taxonomy list on first click
			import("../../../../../services/BookkeepingService").then((mod) => {
				let svc = mod.default || mod;
				if (svc.getCanonicalTaxonomy) {
					svc.getCanonicalTaxonomy().then((result) => {
						this.update({ accounts: result || [] });
					});
				} else {
					this.update({ accounts: [] });
				}
			});
		}
		this.update({ open: !open, search: "" });
	};

	handleSelect = (acct) => {
		let { index, onReclassify } = this.props;
		this.update({ open: false });
		onReclassify({ index: index, canonical_code: acct.canonical_code });
	};

	render() {
	let { item } = this.props;
	let { open, search, accounts } = this.state;
	let classifying = !item.account_name && !item.classification_method;

	let filtered = accounts ? accounts.filter(function (a) {
		if (!search) return true;
		let s = search.toLowerCase();
		return (a.display_name && a.display_name.toLowerCase().indexOf(s) !== -1) || (a.canonical_code && a.canonical_code.toLowerCase().indexOf(s) !== -1);
	}) : [];

	return (
		<td className={clsx("evidence-slot__recon-td--class", !classifying && "evidence-slot__recon-td--clickable")} onClick={classifying ? undefined : this.handleOpen}>
			{classifying ? (
				<span className="evidence-slot__recon-classifying"><Icon.Loader size={10} className="evidence-slot__parsing-spin" /> Classifying...</span>
			) : (
				<span className="evidence-slot__recon-class-text" title="Click to change">{item.account_name || "Unclassified"} <Icon.ChevronDown size={10} /></span>
			)}
			{open && (
				<div className="evidence-slot__recon-dropdown">
					<input
						className="evidence-slot__recon-dropdown-search"
						placeholder="Search accounts..."
						value={search}
						onChange={(e) => { this.update({ search: e.target.value }); }}
						autoFocus
						onClick={function (e) { e.stopPropagation(); }}
					/>
					<div className="evidence-slot__recon-dropdown-list">
						{accounts === null ? (
							<div className="evidence-slot__recon-dropdown-loading">Loading...</div>
						) : filtered.length === 0 ? (
							<div className="evidence-slot__recon-dropdown-loading">No matches</div>
						) : filtered.slice(0, 30).map((a) => {
							return (
								<div key={a.canonical_code} className="evidence-slot__recon-dropdown-item" onClick={(e) => { e.stopPropagation(); this.handleSelect(a); }}>
									{a.display_name || a.canonical_code}
								</div>
							);
						})}
					</div>
				</div>
			)}
		</td>
	);
	}
}

class ReconTable extends WComponent {
	state = {
		selected: new Set(),
		pendingIdx: null,
		filter: "all",
	};

	getFilteredItems() {
		let { items } = this.props;
		let { filter } = this.state;
		return items.map(function (t, i) { return { item: t, originalIndex: i }; }).filter(function (entry) {
			let t = entry.item;
			if (filter === "to_review") return !t.booked && t.classification_status !== "approved";
			if (filter === "approved") return !!t.booked || t.classification_status === "approved";
			return true;
		});
	}

	toggleSelect = (idx) => {
		let { selected } = this.state;
		let next = new Set(selected);
		if (next.has(idx)) next.delete(idx); else next.add(idx);
		this.update({ selected: next });
	};
	toggleSelectAll = () => {
		let { selected } = this.state;
		let filteredItems = this.getFilteredItems();
		let allSelected = filteredItems.length > 0 && filteredItems.every(function (e) { return selected.has(e.originalIndex); });
		if (allSelected) {
			let next = new Set(selected);
			filteredItems.forEach(function (e) { next.delete(e.originalIndex); });
			this.update({ selected: next });
		} else {
			let next = new Set(selected);
			filteredItems.forEach(function (e) { if (!e.item.booked) next.add(e.originalIndex); });
			this.update({ selected: next });
		}
	};

	handleAction = async (idx, action, payload) => {
		let { side, onAction } = this.props;
		this.update({ pendingIdx: idx });
		await onAction({ side, index: idx, action, payload });
		this.update({ pendingIdx: null });
	};

	handleBulk = async (action, payload) => {
		let { items, side, onBulkAction } = this.props;
		let { selected } = this.state;
		let filteredItems = this.getFilteredItems();
		let filteredIndexes = new Set(filteredItems.map(function (e) { return e.originalIndex; }));
		let indexes = Array.from(selected).filter(function (i) { return filteredIndexes.has(i) && !items[i].booked; });
		if (indexes.length === 0) return;
		await onBulkAction({ side, indexes, action, payload });
		this.update({ selected: new Set() });
	};

	render() {
	let { items, title } = this.props;
	let { selected, pendingIdx, filter } = this.state;
	if (!items || items.length === 0) return null;

	let filteredItems = items.map(function (t, i) { return { item: t, originalIndex: i }; }).filter(function (entry) {
		let t = entry.item;
		if (filter === "to_review") return !t.booked && t.classification_status !== "approved";
		if (filter === "approved") return !!t.booked || t.classification_status === "approved";
		return true;
	});

	let toReviewCount = items.filter(function (t) { return !t.booked && t.classification_status !== "approved"; }).length;
	let approvedCount = items.filter(function (t) { return !!t.booked || t.classification_status === "approved"; }).length;
	let filterCounts = { all: items.length, to_review: toReviewCount, approved: approvedCount };

	let allSelected = filteredItems.length > 0 && filteredItems.every(function (e) { return selected.has(e.originalIndex); });
	let someSelected = selected.size > 0;

	return (
		<div className="evidence-slot__recon-table-wrap">
			<div className="evidence-slot__recon-col-head">
				{title} ({items.length})
				<span className="evidence-slot__recon-filters">
					{RECON_FILTERS.map((f) => {
						return (
							<button
								key={f.id}
								className={clsx("evidence-slot__recon-filter", filter === f.id && "evidence-slot__recon-filter--active")}
								onClick={() => { this.update({ filter: f.id, selected: new Set() }); }}
							>
								{f.label} ({filterCounts[f.id] || 0})
							</button>
						);
					})}
				</span>
				{someSelected && (
					<span className="evidence-slot__recon-bulk-actions">
						<span className="evidence-slot__recon-bulk-count">{selected.size} selected</span>
						<button className="evidence-slot__recon-bulk-btn" onClick={() => { this.handleBulk("book"); }} title="Approve selected">
							<Icon.CheckCircle size={12} /> Approve
						</button>
					</span>
				)}
			</div>
			<table className="evidence-slot__recon-table">
				<thead>
					<tr>
						<th className="evidence-slot__recon-th--check"><input type="checkbox" checked={allSelected} onChange={this.toggleSelectAll} /></th>
						<th>Date</th>
						<th>Description</th>
						<th>Classification</th>
						<th>FS Account</th>
						<th className="evidence-slot__recon-th--right">Confidence</th>
						<th className="evidence-slot__recon-th--right">Amount</th>
						<th className="evidence-slot__recon-th--actions">Actions</th>
					</tr>
				</thead>
				<tbody>
					{filteredItems.map((entry) => {
						let t = entry.item;
						let i = entry.originalIndex;
						let method = METHOD_LABELS[t.classification_method] || null;
						let conf = t.confidence_score != null ? Math.round(t.confidence_score * 100) + "%" : "—";
						let classifying = !t.account_name && !t.classification_method;
						let isBooked = !!t.booked;
						let isApproved = t.classification_status === "approved";
						let isPending = pendingIdx === i;

						return (
							<tr key={i} className={clsx(isBooked && "evidence-slot__recon-row--booked")}>
								<td><input type="checkbox" checked={selected.has(i)} onChange={() => { this.toggleSelect(i); }} disabled={isBooked} /></td>
								<td className="evidence-slot__recon-td--date">{t.date || "—"}</td>
								<td className="evidence-slot__recon-td--desc" title={t.description || ""}>{t.description || "—"}</td>
								<ReconClassificationCell
									item={t}
									index={i}
									onReclassify={(args) => { this.handleAction(args.index, "reclassify", { canonical_code: args.canonical_code }); }}
								/>
								<td className="evidence-slot__recon-td--fs">
									{t.account_name || "—"}
									{method && <span className="evidence-slot__recon-method">{method}</span>}
								</td>
								<td className="evidence-slot__recon-td--conf">{classifying ? "—" : conf}</td>
								<td className="evidence-slot__recon-td--amt">{fmtAmt(t.amount)}</td>
								<td className="evidence-slot__recon-td--actions">
									{isPending ? (
										<Icon.Loader size={12} className="evidence-slot__parsing-spin" />
									) : isBooked ? (
										<span className="evidence-slot__recon-status-tag evidence-slot__recon-status-tag--booked">Approved</span>
									) : (
										<span className="evidence-slot__recon-action-btns">
											{t.account_name && !isApproved && (
												<button className="evidence-slot__recon-action-btn" onClick={() => { this.handleAction(i, "book"); }} title="Approve and book to GL">
													<Icon.CheckCircle size={13} />
												</button>
											)}
										</span>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
	}
}

class ReconciliationPanel extends WComponent {
	state = {
		expanded: false,
	};

	render() {
	let { recon, verificationId, onReconAction, onReconBulkAction } = this.props;
	let { expanded } = this.state;
	if (!recon) return null;
	let bankItems = recon.on_bank_not_gl || [];
	let glItems = recon.on_gl_not_bank || [];
	if (bankItems.length === 0 && glItems.length === 0) return null;
	let bankTotal = bankItems.reduce(function (s, t) { return s + (t.amount != null ? Number(t.amount) : 0); }, 0);
	let glTotal = glItems.reduce(function (s, t) { return s + (t.amount != null ? Number(t.amount) : 0); }, 0);

	return (
		<div className="evidence-slot__recon">
			<button className="evidence-slot__recon-toggle" onClick={() => { this.update({ expanded: !expanded }); }}>
				{expanded ? <Icon.ChevronDown size={12} /> : <Icon.ChevronRight size={12} />}
				<Icon.GitMerge size={12} />
				<span>
					Reconciling items: {bankItems.length} on bank not in GL ({fmtAmt(bankTotal)}){glItems.length > 0 ? " | " + glItems.length + " on GL not on bank (" + fmtAmt(glTotal) + ")" : ""}
				</span>
			</button>
			{expanded && (
				<div className="evidence-slot__recon-body">
					<ReconTable items={bankItems} title="On Bank — not in GL" side="bank" verificationId={verificationId} onAction={onReconAction} onBulkAction={onReconBulkAction} />
					<ReconTable items={glItems} title="On GL — not on Bank" side="gl" verificationId={verificationId} onAction={onReconAction} onBulkAction={onReconBulkAction} />
				</div>
			)}
		</div>
	);
	}
}

const PARSING_MESSAGES = [
	"Extracting text from PDF...",
	"Classifying document type...",
	"Reading transactions and balances...",
	"Verifying integrity (running balance arithmetic)...",
	"Checking signs and date ordering...",
	"Comparing against lead sheet...",
	"Building reconciliation...",
	"Almost done...",
];

class ParsingProgress extends WComponent {
	state = {
		msgIndex: 0,
	};

	componentDidMount() {
		this._timer = setInterval(() => {
			this.setState(function (prev) { return { msgIndex: (prev.msgIndex + 1) % PARSING_MESSAGES.length }; });
		}, 2000);
	}

	componentWillUnmount() {
		clearInterval(this._timer);
	}

	render() {
	let { msgIndex } = this.state;
	return (
		<div className="evidence-slot__parsing">
			<Icon.Loader size={13} className="evidence-slot__parsing-spin" />
			<span>{PARSING_MESSAGES[msgIndex]}</span>
		</div>
	);
	}
}

class VerificationSignals extends WComponent {
	state = {
		open: false,
	};

	render() {
	let { signals } = this.props;
	let { open } = this.state;
	let parser = signals.parser || {};
	let integrity = parser.integrity || {};
	let cls = parser.classification || {};
	let pa = signals.period_alignment || {};
	let bc = signals.balance_comparison || {};

	let integrityChecks = [
		{ key: "row_count_check", label: "Row count", ok: integrity.row_count_check ? integrity.row_count_check.ok : null,
			detail: integrity.row_count_check ? "extracted=" + integrity.row_count_check.extracted + " claimed=" + integrity.row_count_check.claimed : null },
		{ key: "amount_derivation", label: "Amount derivation (from running balance)", ok: integrity.amount_derivation ? true : null,
			detail: integrity.amount_derivation ? "derivable=" + integrity.amount_derivation.rows_derivable + " overridden=" + integrity.amount_derivation.rows_overridden + " not_derivable=" + integrity.amount_derivation.rows_not_derivable : null },
		{ key: "sign_check", label: "Sign verification", ok: integrity.sign_check ? integrity.sign_check.ok : null,
			detail: integrity.sign_check ? "pass_rate=" + integrity.sign_check.pass_rate + (integrity.sign_check.normalized ? " (normalized via derivation)" : "") : null },
		{ key: "sequential_balance_check", label: "Sequential balance", ok: integrity.sequential_balance_check ? integrity.sequential_balance_check.ok : null,
			detail: integrity.sequential_balance_check ? "mismatches=" + integrity.sequential_balance_check.mismatch_count : null },
		{ key: "label_consistency_check", label: "Label consistency (opening/closing vs running balances)", ok: integrity.label_consistency_check ? integrity.label_consistency_check.ok : null,
			detail: integrity.label_consistency_check && integrity.label_consistency_check.closing_match ? "closing_delta=" + fmtAmt(integrity.label_consistency_check.closing_match.delta) : null },
		{ key: "opening_to_closing_check", label: "Opening + sum = closing", ok: integrity.opening_to_closing_check ? integrity.opening_to_closing_check.ok : null,
			detail: integrity.opening_to_closing_check ? "computed=" + fmtAmt(integrity.opening_to_closing_check.computed) + " stated=" + fmtAmt(integrity.opening_to_closing_check.stated) + " delta=" + fmtAmt(integrity.opening_to_closing_check.delta) : null },
		{ key: "date_monotonicity_check", label: "Date monotonicity", ok: integrity.date_monotonicity_check ? integrity.date_monotonicity_check.ok : null,
			detail: integrity.date_monotonicity_check ? "out_of_order=" + (integrity.date_monotonicity_check.out_of_order_indexes ? integrity.date_monotonicity_check.out_of_order_indexes.length : 0) : null },
		{ key: "period_boundary_check", label: "Period boundary", ok: integrity.period_boundary_check ? integrity.period_boundary_check.ok : null,
			detail: integrity.period_boundary_check ? "out_of_period=" + (integrity.period_boundary_check.out_of_period_indexes ? integrity.period_boundary_check.out_of_period_indexes.length : 0) : null },
	].filter(function (c) { return c.ok !== null; });

	let passCount = integrityChecks.filter(function (c) { return c.ok === true; }).length;

	return (
		<div className="evidence-slot__signals">
			<button className="evidence-slot__signals-toggle" onClick={() => { this.update({ open: !open }); }}>
				{open ? <Icon.ChevronDown size={11} /> : <Icon.ChevronRight size={11} />}
				<span>Verification signals ({passCount}/{integrityChecks.length} integrity checks passed{parser.normalization_applied ? " | signs normalized" : ""}{parser.transaction_count ? " | " + parser.transaction_count + " transactions" : ""})</span>
			</button>
			{open && (
				<div className="evidence-slot__signals-body">
					{/* Classification */}
					<div className="evidence-slot__signals-section">
						<div className="evidence-slot__signals-title">
							<CheckGlyph ok={cls.doc_type === "bank_statement"} />
							Classification: {cls.doc_type || "unknown"} (confidence: {cls.classification_confidence != null ? (cls.classification_confidence * 100).toFixed(0) + "%" : "?"})
						</div>
						<div className="evidence-slot__signals-rows">
							<div><CheckGlyph ok={cls.has_running_balance_column} /> Running balance column</div>
							<div><CheckGlyph ok={cls.has_period_dates} /> Period dates visible</div>
							<div><CheckGlyph ok={cls.has_account_number} /> Account number visible</div>
						</div>
					</div>

					{/* Integrity checks */}
					<div className="evidence-slot__signals-section">
						<div className="evidence-slot__signals-title">
							<CheckGlyph ok={integrity.overall_pass} />
							Integrity ({passCount}/{integrityChecks.length} checks pass)
						</div>
						<div className="evidence-slot__signals-rows">
							{integrityChecks.map(function (c) {
								return (
									<div key={c.key}>
										<CheckGlyph ok={c.ok} /> {c.label}{c.detail ? " - " + c.detail : ""}
									</div>
								);
							})}
						</div>
						{integrity.caveat && (
							<div className="evidence-slot__signals-caveat">{integrity.caveat}</div>
						)}
					</div>

					{/* Period alignment */}
					{pa.engagement_year_end && (
						<div className="evidence-slot__signals-section">
							<div className="evidence-slot__signals-title">
								<CheckGlyph ok={pa.status === "aligned"} />
								Period alignment: {pa.status || "unknown"}
							</div>
							<div className="evidence-slot__signals-rows">
								<div>Statement: {pa.statement_period_start || "?"} to {pa.statement_period_end || "?"}</div>
								<div>Year-end: {pa.engagement_year_end}</div>
								{pa.gl_date_range && (
									<div>GL range: {pa.gl_date_range.min} to {pa.gl_date_range.max} ({pa.gl_date_range.count} txns)</div>
								)}
							</div>
						</div>
					)}

					{/* Balance comparison */}
					{bc.status && (
						<div className="evidence-slot__signals-section">
							<div className="evidence-slot__signals-title">
								<CheckGlyph ok={bc.status === "matched"} />
								Balance comparison: {bc.status}
							</div>
							<div className="evidence-slot__signals-rows">
								{bc.extracted != null && <div>Extracted: {fmtAmt(bc.extracted)}</div>}
								{bc.expected != null && <div>Expected: {fmtAmt(bc.expected)}</div>}
								{bc.variance != null && bc.variance !== 0 && <div>Variance: {fmtAmt(bc.variance)}</div>}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
	}
}

function getStatementPeriod(d) {
	if (!d || !d.verification || !d.verification.signals || !d.verification.signals.period_alignment) return null;
	let pa = d.verification.signals.period_alignment;
	return { start: pa.statement_period_start, end: pa.statement_period_end };
}

function isInRange(d, yearEnd) {
	if (!yearEnd) return true;
	let pa = d && d.verification && d.verification.signals ? d.verification.signals.period_alignment : null;
	if (!pa || !pa.statement_period_end) return true;
	// In range = statement period end is within the financial year OR the month immediately after (for accrual cutoff)
	let ye = new Date(yearEnd);
	let pe = new Date(pa.statement_period_end);
	if (isNaN(ye.getTime()) || isNaN(pe.getTime())) return true;
	let yearStart = new Date(ye.getFullYear(), ye.getMonth() - 11, 1); // 12 months before year-end
	let accrualCutoff = new Date(ye.getFullYear(), ye.getMonth() + 2, 0); // ~1 month after year-end
	return pe.getTime() >= yearStart.getTime() && pe.getTime() <= accrualCutoff.getTime();
}

function StatementListRow({ d, yearEnd, included, onToggleInclude, isExpanded, onToggleExpand, onDelete, onToggleReviewed, onReconAction, onReconBulkAction, onChecked }) {
	let period = getStatementPeriod(d);
	let v = d.verification;
	let inRange = isInRange(d, yearEnd);
	let vMeta = readVerdictLook(v);
	let closing = v ? v.extractedBalance : null;

	return (
		<div className={clsx("evidence-slot__stmt", !included && "evidence-slot__stmt--excluded", !inRange && "evidence-slot__stmt--out-of-range")}>
			<div className="evidence-slot__stmt-row">
				<input type="checkbox" checked={included} onChange={onToggleInclude} title={included ? "Included in reconciliation" : "Excluded from reconciliation"} />
				<button className="evidence-slot__stmt-expand" onClick={onToggleExpand}>
					{isExpanded ? <Icon.ChevronDown size={12} /> : <Icon.ChevronRight size={12} />}
				</button>
				<Icon.FileText size={13} color={d.reviewed ? "#558855" : "#888"} />
				<span className="evidence-slot__stmt-name">{d.name}.{d.extension}</span>
				{period && period.start && period.end && (
					<span className="evidence-slot__stmt-period">{period.start} to {period.end}</span>
				)}
				{closing != null && (
					<span className="evidence-slot__stmt-balance">{fmtAmt(closing)}</span>
				)}
				{vMeta && (
					<span className={clsx("evidence-slot__stmt-status", vMeta.cls)}>
						<vMeta.icon size={12} /> {vMeta.label}
					</span>
				)}
				{!inRange && (
					<span className="evidence-slot__stmt-tag evidence-slot__stmt-tag--oor">Out of range</span>
				)}
				{v && v.status === "pending" && (
					<span className="evidence-slot__stmt-tag">Parsing...</span>
				)}
				<span className="evidence-slot__stmt-actions">
					<button className={clsx("evidence-slot__btn", "evidence-slot__btn--review", d.reviewed && "evidence-slot__btn--review-active")} onClick={function () { onToggleReviewed(d); }}>
						{d.reviewed ? <Icon.CheckCircle size={12} /> : <Icon.Circle size={12} />}
					</button>
					<button className="evidence-slot__btn evidence-slot__btn--delete" onClick={function () { onDelete(d); }}>
						<Icon.Trash2 size={11} />
					</button>
				</span>
			</div>

			{isExpanded && v && v.status === "pending" && <ParsingProgress />}

			{isExpanded && v && v.status !== "pending" && (
				<div className="evidence-slot__verification">
					{(v.extractedBalance != null || v.expectedBalance != null) && (
						<div className="evidence-slot__compare">
							<div className="evidence-slot__compare-col">
								<div className="evidence-slot__compare-label">On Statement</div>
								<div className="evidence-slot__compare-amount">{v.extractedBalance != null ? fmtAmt(v.extractedBalance) : "—"}</div>
								{v.signals && v.signals.period_alignment && v.signals.period_alignment.statement_period_start && (
									<div className="evidence-slot__compare-period">{v.signals.period_alignment.statement_period_start} to {v.signals.period_alignment.statement_period_end}</div>
								)}
							</div>
							<div className="evidence-slot__compare-col evidence-slot__compare-col--center">
								<div className="evidence-slot__compare-label">Variance</div>
								<div className={clsx("evidence-slot__compare-variance", v.variance != null && Math.abs(v.variance) <= 1 && "evidence-slot__compare-variance--ok")}>
									{v.variance != null ? fmtAmt(v.variance) : "—"}
								</div>
							</div>
							<div className="evidence-slot__compare-col evidence-slot__compare-col--right">
								<div className="evidence-slot__compare-label">On Lead Sheet</div>
								<div className="evidence-slot__compare-amount">{v.expectedBalance != null ? fmtAmt(v.expectedBalance) : "—"}</div>
								{v.signals && v.signals.period_alignment && v.signals.period_alignment.engagement_year_end && (
									<div className="evidence-slot__compare-period">Year-end: {v.signals.period_alignment.engagement_year_end}</div>
								)}
							</div>
						</div>
					)}
					<div className="evidence-slot__finding">
						<div className={clsx("evidence-slot__finding-status", vMeta ? vMeta.cls : "evidence-slot__vmeta--default")}>
							{vMeta && <vMeta.icon size={14} />}
							<span>{vMeta ? vMeta.label : v.status}</span>
							{v.confidence && (
								<span className={clsx("evidence-slot__confidence-badge", "evidence-slot__confidence-badge--" + v.confidence)}>
									{v.confidence}{v.signals && v.signals.confidence_score != null ? " (" + v.signals.confidence_score + ")" : ""}
								</span>
							)}
						</div>
						{v.whatToDoNext && (
							<div className="evidence-slot__finding-action">{v.whatToDoNext}</div>
						)}
						{v.canCheckAgain && (
							<CheckAgainButton documentId={d.id} onChecked={onChecked} className="evidence-slot__recheck" />
						)}
					</div>
					{v.signals && v.signals.reconciliation && (
						<ReconciliationPanel recon={v.signals.reconciliation} verificationId={v.id} onReconAction={onReconAction} onReconBulkAction={onReconBulkAction} />
					)}
					{v.signals && <VerificationSignals signals={v.signals} />}
				</div>
			)}
		</div>
	);
}

class EvidenceSlot extends WComponent {
	state = {
		verificationOpen: false,
		expandedDocId: null,
	};

	fileInputRef = React.createRef();

	// Computes the dependency value the original useEffect watched, so we can
	// reproduce its trigger condition across componentDidMount/componentDidUpdate.
	autoExcludeDepValue() {
		let { allDocs } = this.props;
		return allDocs ? allDocs.map(function (d) { return d.id + ":" + (d.verification ? d.verification.status : "none"); }).join(",") : "";
	}

	// Auto-exclude out-of-range statements on first verification completion.
	// Only fires once per doc (when verification transitions from pending to resolved)
	// and only if not already persisted. Uses the API to persist so it survives refresh.
	runAutoExcludeEffect = () => {
		let { allDocs, yearEnd, onLoadDocuments } = this.props;
		let multiDoc = allDocs && allDocs.length > 1;
		// yearEnd comes from engagement.fiscal_year_end via the lead sheets API.
		// This is the authoritative source — set when the user clicks "Generate AFS".
		let effectiveYearEnd = yearEnd;
		if (!multiDoc || !effectiveYearEnd || !allDocs) return;
		for (let d of allDocs) {
			if (d.verification && d.verification.status !== "pending" && d.verification.excluded === undefined) {
				if (!isInRange(d, effectiveYearEnd)) {
					BookkeepingService.toggleStatementExclusion({ verificationId: d.verification.id, excluded: true }).then(function () {
						if (onLoadDocuments) onLoadDocuments();
					});
				}
			}
		}
	};

	componentDidMount() {
		this.runAutoExcludeEffect();
	}

	componentDidUpdate(prevProps) {
		let prevDep = prevProps.allDocs ? prevProps.allDocs.map(function (d) { return d.id + ":" + (d.verification ? d.verification.status : "none"); }).join(",") : "";
		if (prevDep !== this.autoExcludeDepValue()) {
			this.runAutoExcludeEffect();
		}
	}

	render() {
	let { slot, doc, allDocs, yearEnd, uploading, onUpload, onDelete, onToggleReviewed, onReconAction, onReconBulkAction, onLoadDocuments, readOnly } = this.props;
	let { verificationOpen, expandedDocId } = this.state;
	let multiDoc = allDocs && allDocs.length > 1;

	// yearEnd comes from engagement.fiscal_year_end via the lead sheets API.
	// This is the authoritative source — set when the user clicks "Generate AFS".
	let effectiveYearEnd = yearEnd;

	function isDocExcluded(d) {
		return d && d.verification && d.verification.excluded === true;
	}

	let handleToggleExclude = async (d) => {
		let v = d && d.verification;
		if (!v || !v.id) return;
		await BookkeepingService.toggleStatementExclusion({ verificationId: v.id, excluded: !isDocExcluded(d) });
		if (onLoadDocuments) onLoadDocuments();
	};

	function handleFile(e) {
		let files = e.target.files;
		if (!files || files.length === 0) return;
		for (let i = 0; i < files.length; i++) {
			onUpload(files[i]);
		}
		if (e.target) e.target.value = "";
	}

	function fmtSize(size) {
		if (!size) return "";
		if (size > 1048576) return (size / 1048576).toFixed(1) + " MB";
		return Math.round(size / 1024) + " KB";
	}

	let isWaived = slot.waived;
	let hasDoc = !!doc || (allDocs && allDocs.length > 0);
	let verification = doc && doc.verification ? doc.verification : null;
	let vMeta = readVerdictLook(verification);

	return (
		<div className={clsx(
			"evidence-slot",
			isWaived && "evidence-slot--waived",
			hasDoc && !isWaived && "evidence-slot--filled",
			!hasDoc && !isWaived && slot.required && "evidence-slot--required-empty",
			doc && doc.reviewed && "evidence-slot--reviewed",
		)}>
			<div className="evidence-slot__main">
				<div className="evidence-slot__label-block">
					<span className="evidence-slot__label">{slot.label}</span>
					{slot.required && !isWaived && (
						<span className="evidence-slot__badge evidence-slot__badge--required">Required</span>
					)}
					{!slot.required && !isWaived && (
						<span className="evidence-slot__badge evidence-slot__badge--optional">Optional</span>
					)}
					{isWaived && (
						<span className="evidence-slot__badge evidence-slot__badge--waived">Waived</span>
					)}
					{multiDoc && (
						<span className="evidence-slot__badge">{allDocs.length} statements</span>
					)}
				</div>

				<div className="evidence-slot__action">
					{hasDoc && !multiDoc && (
						<div className="evidence-slot__doc">
							<Icon.FileText size={13} color={doc && doc.reviewed ? "#558855" : "#888"} />
							<span className="evidence-slot__doc-name" title={doc ? doc.name + "." + doc.extension : ""}>
								{doc ? doc.name + "." + doc.extension : ""}
							</span>
							<span className="evidence-slot__doc-size">{doc ? fmtSize(doc.size) : ""}</span>
							{vMeta && (
								<button className={clsx("evidence-slot__btn", "evidence-slot__btn--verification", vMeta.cls)} onClick={() => { this.update({ verificationOpen: !verificationOpen }); }} title={vMeta.label}>
									<vMeta.icon size={13} />
								</button>
							)}
							<button className={clsx("evidence-slot__btn", "evidence-slot__btn--review", doc && doc.reviewed && "evidence-slot__btn--review-active")} onClick={function () { onToggleReviewed(doc); }}>
								{doc && doc.reviewed ? <Icon.CheckCircle size={13} /> : <Icon.Circle size={13} />}
							</button>
							{!readOnly && (
								<button className="evidence-slot__btn evidence-slot__btn--delete" onClick={function () { onDelete(doc); }}>
									<Icon.Trash2 size={12} />
								</button>
							)}
						</div>
					)}

					{!readOnly && (
						<label className={clsx("evidence-slot__upload", uploading && "evidence-slot__upload--disabled")}>
							<Icon.Upload size={12} /> {uploading ? "Uploading..." : (hasDoc ? "Add" : "Upload")}
							<input ref={this.fileInputRef} type="file" multiple className="evidence-slot__file-input-hidden" onChange={handleFile} disabled={uploading} />
						</label>
					)}
					{readOnly && !hasDoc && (
						<span className="evidence-slot__readonly-hint">
							Upload on Documentation tab →
						</span>
					)}

					{isWaived && (
						<span className="evidence-slot__waived-note" title={slot.justification || ""}>
							{slot.justification ? slot.justification : "No justification recorded"}
						</span>
					)}
				</div>
			</div>

			{/* Multi-doc: statement list */}
			{multiDoc && (
				<div className="evidence-slot__stmt-list">
					{allDocs.map(function (d) {
						return (
							<StatementListRow
								key={d.id}
								d={d}
								yearEnd={effectiveYearEnd}
								included={!isDocExcluded(d)}
								onToggleInclude={function () { handleToggleExclude(d); }}
								isExpanded={expandedDocId === d.id}
								onToggleExpand={() => { this.update({ expandedDocId: expandedDocId === d.id ? null : d.id }); }}
								onDelete={onDelete}
								onToggleReviewed={onToggleReviewed}
								onReconAction={onReconAction}
								onReconBulkAction={onReconBulkAction}
								onChecked={onLoadDocuments}
							/>
						);
					})}
				</div>
			)}

			{/* Single-doc: inline verification */}
			{!multiDoc && verification && verification.status === "pending" && (
				<ParsingProgress />
			)}

			{!multiDoc && verification && verificationOpen && verification.status !== "pending" && (
				<div className="evidence-slot__verification">
					{/* Level 1: Comparison strip */}
					{(verification.extractedBalance != null || verification.expectedBalance != null) && (
						<div className="evidence-slot__compare">
							<div className="evidence-slot__compare-col">
								<div className="evidence-slot__compare-label">On Statement</div>
								<div className="evidence-slot__compare-amount">{verification.extractedBalance != null ? fmtAmt(verification.extractedBalance) : "—"}</div>
								{verification.signals && verification.signals.period_alignment && verification.signals.period_alignment.statement_period_start && (
									<div className="evidence-slot__compare-period">
										{verification.signals.period_alignment.statement_period_start} to {verification.signals.period_alignment.statement_period_end}
									</div>
								)}
							</div>
							<div className="evidence-slot__compare-col evidence-slot__compare-col--center">
								<div className="evidence-slot__compare-label">Variance</div>
								<div className={clsx("evidence-slot__compare-variance", verification.variance != null && Math.abs(verification.variance) <= 1 && "evidence-slot__compare-variance--ok")}>
									{verification.variance != null ? fmtAmt(verification.variance) : "—"}
								</div>
							</div>
							<div className="evidence-slot__compare-col evidence-slot__compare-col--right">
								<div className="evidence-slot__compare-label">On Lead Sheet</div>
								<div className="evidence-slot__compare-amount">{verification.expectedBalance != null ? fmtAmt(verification.expectedBalance) : "—"}</div>
								{verification.signals && verification.signals.period_alignment && verification.signals.period_alignment.engagement_year_end && (
									<div className="evidence-slot__compare-period">
										Year-end: {verification.signals.period_alignment.engagement_year_end}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Level 2: Primary finding + next action */}
					<div className="evidence-slot__finding">
						<div className={clsx("evidence-slot__finding-status", vMeta ? vMeta.cls : "evidence-slot__vmeta--default")}>
							{vMeta && <vMeta.icon size={14} />}
							<span>{vMeta ? vMeta.label : verification.status}</span>
							{verification.confidence && (
								<span className={clsx("evidence-slot__confidence-badge", "evidence-slot__confidence-badge--" + verification.confidence)}>
									{verification.confidence}{verification.signals && verification.signals.confidence_score != null ? " (" + verification.signals.confidence_score + ")" : ""}
								</span>
							)}
						</div>
						{verification.whatToDoNext && (
							<div className="evidence-slot__finding-action">{verification.whatToDoNext}</div>
						)}
						{verification.canCheckAgain && doc && (
							<CheckAgainButton documentId={doc.id} onChecked={onLoadDocuments} className="evidence-slot__recheck" />
						)}
					</div>

					{verification.errorDetail && (
						<div className="evidence-slot__finding-action evidence-slot__finding-action--muted">{verification.errorDetail}</div>
					)}

					{/* Level 3: Collapsed drill-downs */}
					{verification.signals && verification.signals.reconciliation && (
						<ReconciliationPanel
							recon={verification.signals.reconciliation}
							verificationId={verification.id || (doc && doc.verificationId)}
							onReconAction={onReconAction}
							onReconBulkAction={onReconBulkAction}
						/>
					)}
					{verification.signals && (
						<VerificationSignals signals={verification.signals} />
					)}
				</div>
			)}
		</div>
	);
	}
}

export default EvidenceSlot;
